import React from 'react';
import { motion } from 'framer-motion';
import { FaPhone, FaRupeeSign, FaCheckCircle, FaTimesCircle } from 'react-icons/fa';

const PaymentLoader = ({ status, amount }) => {
  const messages = {
    processing: 'Processing your payment with PhonePe...',
    verifying: 'Verifying payment with PhonePe...',
    success: 'Payment Successful! 🎉',
    failed: 'Payment Failed ❌'
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl"
      >
        {/* PhonePe Logo Animation */}
        <div className="text-center mb-6">
          <motion.div
            animate={{ 
              scale: [1, 1.1, 1],
              rotate: [0, 360]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              ease: "easeInOut"
            }}
            className="w-20 h-20 bg-gradient-to-r from-purple-600 to-blue-500 rounded-2xl flex items-center justify-center mx-auto shadow-lg"
          >
            <FaPhone className="text-white text-3xl" />
          </motion.div>
          <h3 className="text-xl font-bold text-gray-800 mt-3">PhonePe</h3>
          <p className="text-sm text-gray-500">Secure Payment</p>
        </div>

        {/* Amount Display */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-gray-50 rounded-xl p-4 mb-6 text-center"
        >
          <p className="text-sm text-gray-500">Amount</p>
          <p className="text-3xl font-bold text-gray-800">
            <FaRupeeSign className="inline text-xl" /> {amount}
          </p>
        </motion.div>

        {/* Status Animation */}
        <div className="flex flex-col items-center">
          {status === 'processing' && (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
              className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full"
            />
          )}
          {status === 'verifying' && (
            <motion.div
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ duration: 0.5, repeat: Infinity }}
              className="flex items-center gap-2"
            >
              <div className="w-3 h-3 bg-purple-500 rounded-full" />
              <div className="w-3 h-3 bg-purple-500 rounded-full animation-delay-200" />
              <div className="w-3 h-3 bg-purple-500 rounded-full animation-delay-400" />
            </motion.div>
          )}
          {status === 'success' && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              <FaCheckCircle className="text-green-500 text-6xl" />
            </motion.div>
          )}
          {status === 'failed' && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200 }}
            >
              <FaTimesCircle className="text-red-500 text-6xl" />
            </motion.div>
          )}
          
          <p className="mt-4 text-gray-700 font-medium">
            {messages[status] || 'Processing...'}
          </p>
        </div>

        {/* Progress Bar */}
        {(status === 'processing' || status === 'verifying') && (
          <motion.div
            className="mt-6 h-2 bg-gray-200 rounded-full overflow-hidden"
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: '100%' }}
              transition={{ duration: 3, ease: "easeInOut" }}
              className="h-full bg-gradient-to-r from-purple-500 to-blue-500"
            />
          </motion.div>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentLoader;
